import { useStyles } from '@archipro-design/aria';
import { cn } from '@rocco/utils/cn';
import { COLUMN_WIDTH_100 } from '@modules/page-builder/config/column-constants';
import type { ImageColumnProps } from '@modules/page-builder/types';
import DetailSingleImage from '../detail-single-image-shell/DetailSingleImageShell';
import { FullPageImageContainer } from '../../common/pagebuilder.style';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import ImageWithPaintPicker from '../../common/image-with-paint-picker/ImageWithPaintPicker';
import { ImageModuleExtraSpacing } from '@modules/page-builder/component/detail-mobile/detail-modules/DetailModules.style';

interface FullPageImageModuleProps {
    column: ImageColumnProps;
    /** Project detail revamp (BC-4725) — restyles the caption to the Figma text style. */
    isProjectRevamp?: boolean;
}

const FullPageImageModule = ({
    column,
    isProjectRevamp,
}: FullPageImageModuleProps) => {
    const { css } = useStyles();

    if (!column.imageUrl) {
        return null;
    }

    return (
        // The shared tile panel rounds its hover scrims to `--tile-corner` and
        // falls back to 2rem when nobody publishes one. Declared on the module's
        // outermost element so it reaches the panel wherever inside the module
        // it mounts: this image is a deliberate edge-to-edge bleed, and a
        // rounded scrim over a square photograph notches its corners on hover.
        <div
            className={cn(
                css(FullPageImageContainer, ImageModuleExtraSpacing),
                isProjectRevamp && '[--tile-corner:0rem]',
            )}
        >
            <DetailSingleImage
                caption={column.caption}
                libraryImage={column?.libraryImage}
                tags={column.tags}
                uniqueApprovedTags={column.uniqueApprovedTags}
                imageURL={column.imageUrl}
                dimension={column.dimension}
                columnId={column.id}
                moduleType="FullPageImageModule"
                isProjectRevamp={isProjectRevamp}
            >
                <ImageWithPaintPicker
                    src={column.imageUrl}
                    width={column.dimension.width}
                    height={column.dimension.height}
                    layout={'fill'}
                    objectFit={'cover'}
                    sizes={getImagesSizes(COLUMN_WIDTH_100)}
                    alt={column.caption || column?.libraryImage?.Title || ''}
                />
            </DetailSingleImage>
        </div>
    );
};

export default FullPageImageModule;
